import { CreateCalculationRequestSheet } from '@/components/features/CalculationRequests/forms/CreateCalculationRequestSheet';
import type { CalculationRequest, CalculationRequestOptions } from '@/components/features/CalculationRequests/types';
import LeadCalculationRequests from '@/components/features/Leads/components/LeadCalculationRequests';
import OffersList from '@/components/features/Leads/components/OffersList';
import CreateOfferModal from '@/components/features/Leads/forms/CreateOfferModal';
import CreateProjectFromLeadSheet from '@/components/features/Leads/forms/CreateProjectFromLeadSheet';
import EditLeadModal from '@/components/features/Leads/forms/EditLeadModal';
import type { LeadDefaults } from '@/components/features/Projects/forms/ProjectForm';
import { AddressMapThumbnail } from '@/components/global/AddressMapThumbnail';
import { PersonSelect, type PersonOption } from '@/components/global/PersonSelect';
import UniversalComments from '@/components/global/comments/UniversalComments';
import { DeleteActionButton, DeleteConfirmAlert, DETAIL_ACTION_BUTTON_CLASS } from '@/components/global/detail-page-actions';
import { type CalculationRequestOption } from '@/components/global/selectors/CalculationRequestSelect';
import { UnifiedPrioritySelector } from '@/components/global/selectors/UnifiedPrioritySelector';
import { UnifiedStatusSelector } from '@/components/global/selectors/UnifiedStatusSelector';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/shadcn/alert';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/shadcn/drawer';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/shadcn/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs';
import { useSettings } from '@/contexts/SettingsContext';
import { useIsTabletOrMobile } from '@/hooks/use-mobile';
import { useNavigationVisibilityUi, type NavVisibilityKey } from '@/hooks/useNavigationVisibilityUi';
import { useTranslations } from '@/hooks/useTranslations';
import ShowLayout from '@/layouts/show-layout';
import { getCountryName } from '@/lib/country-utils';
import { settingLabel } from '@/lib/status-labels';
import { cn, formatCurrency, formatDate, formatDateForDisplay, useCan } from '@/lib/utils';
import { Leads, type BreadcrumbItem, type Company, type LeadOffer, type People, type User } from '@/types';
import { Head, Link, router, usePage } from '@inertiajs/react';
import { format } from 'date-fns';
import {
    AlertCircle,
    Building2,
    Calculator,
    CalendarClock,
    Edit,
    FileText,
    Flag,
    Globe,
    House,
    Info,
    Mail,
    Mailbox,
    MapPin,
    MoreHorizontal,
    Phone,
    Receipt,
    Trash2,
    TrendingUp,
    User as UserIcon,
    UserRound,
    Wallet,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

/**
 * Winning ends the selling: the price is settled, so the lead takes no further
 * offer and no further calculation. Converting it to a project is the one thing
 * that stays open — mirrors `Lead::isSellingClosed()` on the server, which has
 * the last word.
 *
 * A converted lead counts as closed whatever its status says, for the rows
 * written before winning became a prerequisite.
 */
const isSellingClosed = (lead: Leads): boolean => lead.status === 'won' || Boolean(lead.project);

type CalculationRequestFormOptions = Pick<CalculationRequestOptions, 'phases' | 'leads'>;
type CalculationRequestTableOptions = Pick<CalculationRequestOptions, 'statuses' | 'phases' | 'complexities' | 'engineers'>;

interface ShowLeadPageProps {
    lead: Leads;
    latestApprovedOffer?: LeadOffer | null;
    companies: Company[];
    people: People[];
    users: User[];
    leadOfferApprovalThreshold?: number;
    /** Calculation requests currently attached to this lead — full rows, for the tab's table. */
    linkedCalculationRequests?: CalculationRequest[];
    /** The pick list — deferred, so it is absent on first paint. */
    calculationRequestOptions?: CalculationRequestOption[];
    /** Badge catalogues and the engineer list the calculations table renders with. */
    calculationRequestTableOptions?: CalculationRequestTableOptions;
    canViewCalculationFinancials?: boolean;
    canLinkCalculationRequests?: boolean;
    /** Just enough of the create form's option payload for the "New calculation" sheet. */
    calculationRequestFormOptions?: CalculationRequestFormOptions;
    /** Contacts registered against this lead's company — the contact picker's list. */
    contactCandidates?: PersonOption[];
    /** Everyone active, with the commercially-responsible roles surfaced first. */
    ownerCandidates?: { people: PersonOption[]; suggested_ids: string[] };
    /** This lead's values for the conversion sheet; null once already converted. */
    projectFormDefaults?: LeadDefaults | null;
    /**
     * Whether this viewer may hand the lead to someone else — narrower than
     * `leads.update`, and decided server-side because the creator is not serialized.
     */
    canChangeOwner?: boolean;
    [key: string]: unknown;
}

interface LeadTab {
    value: string;
    label: string;
    displayLabel: ReactNode;
    count?: number;
    hiddenFromMainNav: boolean;
    content: ReactNode;
}

interface LeadDetailsCardsProps {
    lead: Leads;
    latestApprovedOffer?: LeadOffer | null;
    users: User[];
    currentUser?: User;
    contactCandidates: PersonOption[];
    ownerCandidates: { people: PersonOption[]; suggested_ids: string[] };
    canChangeOwner: boolean;
}

/** A labelled value in the detail cards. Renders an em dash when empty. */
function DetailField({ label, icon, children }: { label: string; icon?: ReactNode; children?: ReactNode }) {
    return (
        <div className="space-y-1">
            <div className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
                {icon}
                {label}
            </div>
            <div className="text-sm font-medium break-words">{children ?? <span className="text-muted-foreground">—</span>}</div>
        </div>
    );
}

/** One tile in the headline metrics strip: coloured icon, label, value. */
function MetricTile({ icon, tone, label, children }: { icon: ReactNode; tone: string; label: string; children: ReactNode }) {
    return (
        <div className="flex items-center gap-3 p-4">
            <span className={cn('inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', tone)}>{icon}</span>
            <div className="min-w-0">
                <div className="text-muted-foreground text-xs font-medium">{label}</div>
                <div className="mt-0.5 text-lg font-semibold">{children}</div>
            </div>
        </div>
    );
}

function LeadDetailsCards({
    lead,
    latestApprovedOffer,
    users,
    currentUser,
    contactCandidates,
    ownerCandidates,
    canChangeOwner,
}: LeadDetailsCardsProps) {
    const { t } = useTranslations();
    const { settings } = useSettings();
    const { can } = useCan();

    // The prompt is only worth making while an offer can still be made — a won
    // lead that never got one is past the point of being nudged.
    const hasNoOffers = !latestApprovedOffer && (!lead.offers || lead.offers.length === 0) && !isSellingClosed(lead);
    // Only a live lead can miss its deadline; won/lost ones are already resolved.
    const offerDeadlineOverdue = useMemo(() => {
        if (!lead.offer_due_date || lead.status === 'won' || lead.status === 'lost') {
            return false;
        }

        return String(lead.offer_due_date).slice(0, 10) < formatDate(new Date());
    }, [lead.offer_due_date, lead.status]);
    const hasAddressOrCoordinates = Boolean(
        lead.address_street || lead.address_house_number || lead.address_city || lead.address_lat || lead.address_lng,
    );

    const addressLabel = [lead.address_street, lead.address_house_number].filter(Boolean).join(' ');
    const canCreateOffer = can('leads.update') && !isSellingClosed(lead);
    const contactPhone = typeof lead.person?.phone === 'string' && lead.person.phone ? lead.person.phone : null;

    // category is a list on Company; render the stored values through the settings labels.
    const companyCategoryLabel = useMemo(() => {
        const raw = lead.company?.category;
        const values = Array.isArray(raw) ? raw : raw ? [String(raw)] : [];

        return values.length > 0 ? values.map((value) => settingLabel(t, 'company_categories', value)).join(', ') : null;
    }, [lead.company?.category, t]);

    // Converted leads are locked, the same rule the action buttons use.
    const canEditPeople = can('leads.update') && !lead.project;

    /**
     * Whoever is set stays representable even if they have since fallen out of the
     * candidate set — a contact moved to another company, a suspended owner —
     * otherwise the picker would render an empty value over real data.
     */
    const contactPeople = useMemo(() => {
        const byId = new Map(contactCandidates.map((person) => [person.id, person]));
        // Added only when missing: the candidate entry carries the position badge,
        // which the record's own relation does not.
        if (lead.person && !byId.has(lead.person.id)) {
            byId.set(lead.person.id, {
                id: lead.person.id,
                first_name: lead.person.first_name,
                last_name: lead.person.last_name,
            });
        }

        return Array.from(byId.values());
    }, [contactCandidates, lead.person]);

    const ownerPeople = useMemo(() => {
        const byId = new Map(ownerCandidates.people.map((person) => [person.id, person]));
        // Added only when missing, so the candidate entry keeps its role badge.
        if (lead.lead_owner && !byId.has(lead.lead_owner.id)) {
            byId.set(lead.lead_owner.id, {
                id: lead.lead_owner.id,
                first_name: lead.lead_owner.first_name,
                last_name: lead.lead_owner.last_name,
            });
        }

        return Array.from(byId.values());
    }, [ownerCandidates.people, lead.lead_owner]);

    const updateContact = (personId: string | null) => {
        router.patch(
            route('leads.update-contact', lead.id),
            { person_id: personId },
            {
                preserveScroll: true,
                onSuccess: () => toast.success(t('leads.flash.contact_updated', 'Contact person updated.')),
                onError: (errors) => toast.error(errors.person_id ?? t('leads.flash.contact_failed', 'Failed to update the contact person.')),
            },
        );
    };

    const updateOwner = (userId: string | null) => {
        router.patch(
            route('leads.update-owner', lead.id),
            { lead_owner_id: userId },
            {
                preserveScroll: true,
                onSuccess: () => toast.success(t('leads.flash.owner_updated', 'Lead owner updated.')),
                onError: (errors) => toast.error(errors.lead_owner_id ?? t('leads.flash.owner_failed', 'Failed to update the lead owner.')),
            },
        );
    };

    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-4 lg:items-start">
            {/* Main column — the lead itself gets ~75% of the working area. */}
            <div className="space-y-4 lg:col-span-3">
                {/* The two states that need acting on lead the column; the figures they
                    used to sit under now live in the headline strip above. */}
                {hasNoOffers && (
                    <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>{t('leads.sidebar.no_offer_title', 'No Offer Created')}</AlertTitle>
                        <AlertDescription className="space-y-3">
                            <p>
                                {t(
                                    'leads.sidebar.no_offer_description',
                                    'An offer needs to be created to track estimated value and costs for this lead.',
                                )}
                            </p>
                            {canCreateOffer && (
                                <CreateOfferModal
                                    lead={lead}
                                    trigger={
                                        <Button type="button" variant="outline" size="sm" className="gap-2">
                                            <FileText className="h-4 w-4" />
                                            {t('leads.sidebar.create_offer_cta', 'Create offer')}
                                        </Button>
                                    }
                                />
                            )}
                        </AlertDescription>
                    </Alert>
                )}

                {/* Only until it becomes a project — from then on the action row
                    says so itself, and two notices for one state is one too many. */}
                {lead.status === 'won' && !lead.project && (
                    <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>{t('leads.sidebar.won_locked_title', 'Lead Won')}</AlertTitle>
                        <AlertDescription>
                            {t(
                                'leads.sidebar.won_locked_description',
                                'The price is settled, so no new offers or calculations can be added. Converting the lead to a project is still available.',
                            )}
                        </AlertDescription>
                    </Alert>
                )}

                {lead.status === 'lost' && lead.lost_reason && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>{t('leads.sidebar.lost_reason_title', 'Lead Lost')}</AlertTitle>
                        <AlertDescription>{String(lead.lost_reason)}</AlertDescription>
                    </Alert>
                )}

                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle>{t('leads.sidebar.lead_information', 'Lead information')}</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
                        {/* What the object is called — the calculations and the converted project take it too. */}
                        <DetailField label={t('leads.sidebar.object_name', 'Object name')}>{lead.name || lead.lead_number}</DetailField>

                        <DetailField label={t('leads.sidebar.offer_due_date', 'Offer deadline')}>
                            {lead.offer_due_date ? (
                                <span className={cn(offerDeadlineOverdue && 'text-destructive')}>
                                    {formatDateForDisplay(String(lead.offer_due_date).slice(0, 10))}
                                </span>
                            ) : null}
                        </DetailField>

                        <DetailField label={t('leads.sidebar.last_contacted', 'Last contact')}>
                            {lead.last_contacted ? format(new Date(String(lead.last_contacted)), 'yyyy.MM.dd') : null}
                        </DetailField>

                        <DetailField label={t('leads.sidebar.source', 'Source')}>{lead.lead_source ? String(lead.lead_source) : null}</DetailField>

                        <DetailField label={t('leads.sidebar.related_project', 'Related project')}>
                            {lead.project ? (
                                <Link href={`/projects/${lead.project.id}`} className="text-primary hover:underline">
                                    {lead.project.contract_number || lead.project.contract_name}
                                </Link>
                            ) : (
                                <span className="text-muted-foreground">—</span>
                            )}
                        </DetailField>

                        <DetailField label={t('leads.sidebar.created', 'Created')}>
                            {lead.created_at ? format(new Date(lead.created_at), 'yyyy.MM.dd HH:mm') : null}
                        </DetailField>

                        <div className="sm:col-span-2 xl:col-span-3">
                            <DetailField label={t('leads.sidebar.description', 'Description')}>
                                {lead.notes ? (
                                    <span className="font-normal whitespace-pre-wrap">{String(lead.notes)}</span>
                                ) : (
                                    <span className="text-muted-foreground font-normal">
                                        {t('leads.sidebar.no_description', 'No description provided')}
                                    </span>
                                )}
                            </DetailField>
                        </div>
                    </CardContent>
                </Card>

                {/* Comments sit alongside the company card rather than in their own
                    tab, so the whole picture is on one screen. */}
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    {/* The card is taken out of flow on desktop so a long thread can never
                        stretch the row taller than the card beside it — it fills the
                        wrapper, whose height comes from that card (min 26rem). */}
                    <div className="relative lg:min-h-[26rem]">
                        <Card className="flex max-h-[67dvh] flex-col gap-4 overflow-hidden pb-0 lg:absolute lg:inset-0 lg:max-h-none">
                            <CardHeader>
                                <CardTitle>{t('leads.show.tabs.comments', 'Comments')}</CardTitle>
                            </CardHeader>
                            <CardContent className="flex min-h-0 flex-1 flex-col px-0">
                                <UniversalComments model="Lead" entityId={lead.id} users={users} currentUser={currentUser} layout="container" />
                            </CardContent>
                        </Card>
                    </div>

                    <div className="space-y-4 lg:col-span-2">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between gap-3 pb-3">
                                <CardTitle>{t('leads.sidebar.about_company', 'About the company')}</CardTitle>
                                {lead.company && (
                                    <Link href={`/companies/${lead.company.id}`} className="text-primary text-sm hover:underline">
                                        {t('leads.sidebar.view_company', 'View company')}
                                    </Link>
                                )}
                            </CardHeader>
                            <CardContent>
                                {lead.company ? (
                                    <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
                                        <DetailField label={t('leads.sidebar.company_category', 'Category')}>{companyCategoryLabel}</DetailField>
                                        <DetailField label={t('leads.sidebar.company_type', 'Type')}>
                                            {lead.company.type ? settingLabel(t, 'company_types', lead.company.type) : null}
                                        </DetailField>
                                        <DetailField label={t('leads.sidebar.company_employees', 'Employees')}>
                                            {lead.company.employees ? String(lead.company.employees) : null}
                                        </DetailField>
                                        <DetailField label={t('leads.sidebar.company_revenue', 'Revenue')}>
                                            {lead.company.revenue ? formatCurrency(Number(lead.company.revenue)) : null}
                                        </DetailField>
                                        <DetailField label={t('leads.sidebar.company_founded', 'Founded')}>
                                            {lead.company.founded ? String(lead.company.founded) : null}
                                        </DetailField>
                                        <DetailField label={t('leads.sidebar.country', 'Country')}>
                                            {lead.company.country
                                                ? getCountryName(lead.company.country, settings.countries) || lead.company.country
                                                : null}
                                        </DetailField>
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground text-sm">
                                        {t('leads.sidebar.no_company_details', 'Link a company to see its details here.')}
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>

            {/* Context column — company and address stay out of the way at ~25%. */}
            <div className="space-y-4 lg:col-span-1">
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle>{t('leads.sidebar.company_contact', 'Company & Contact')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-start gap-2.5">
                            <Building2 className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                            <div className="min-w-0">
                                <div className="text-muted-foreground text-xs font-medium">{t('leads.sidebar.company', 'Company')}</div>
                                <div className="text-sm font-medium">
                                    {lead.company ? (
                                        <Link href={`/companies/${lead.company.id}`} className="text-primary hover:underline">
                                            {lead.company.name}
                                        </Link>
                                    ) : (
                                        <span className="text-muted-foreground">{t('leads.sidebar.no_company', 'No company assigned')}</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Only this company's contacts are eligible — a contact from
                            another company on this lead means nothing. */}
                        <div className="flex items-start gap-2.5">
                            <UserIcon className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                            <div className="min-w-0 flex-1">
                                <div className="text-muted-foreground text-xs font-medium">{t('leads.sidebar.contact_person', 'Contact Person')}</div>
                                <PersonSelect
                                    value={lead.person_id ?? null}
                                    onValueChange={updateContact}
                                    people={contactPeople}
                                    disabled={!canEditPeople}
                                    variant="inline"
                                    allowClear={false}
                                    showUnassignedOption
                                    showRole
                                    className="-ml-1.5"
                                    labels={{
                                        placeholder: t('leads.sidebar.no_person', 'No person assigned'),
                                        search: t('leads.sidebar.contact_person_search', 'Search contacts…'),
                                        empty: t('leads.sidebar.contact_person_empty', 'This company has no contacts yet.'),
                                        peopleHeading: t('leads.sidebar.contact_person_heading', 'Company contacts'),
                                        unassignedOption: t('leads.sidebar.no_person', 'No person assigned'),
                                    }}
                                />
                            </div>
                        </div>

                        {/* Anyone active can own a lead; the picker just puts the
                            commercially-responsible roles first. Who may *move* it is
                            narrower than who may edit the lead — see `canChangeOwner`. */}
                        <div className="flex items-start gap-2.5">
                            <UserRound className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                            <div className="min-w-0 flex-1">
                                <div className="text-muted-foreground text-xs font-medium">{t('leads.sidebar.lead_owner', 'Lead Owner')}</div>
                                <PersonSelect
                                    value={lead.lead_owner_id ?? null}
                                    onValueChange={updateOwner}
                                    people={ownerPeople}
                                    suggestedIds={ownerCandidates.suggested_ids}
                                    disabled={!canChangeOwner}
                                    variant="inline"
                                    allowClear={false}
                                    showUnassignedOption
                                    showRole
                                    className="-ml-1.5"
                                    labels={{
                                        placeholder: t('leads.sidebar.no_owner', 'No owner assigned'),
                                        search: t('leads.sidebar.lead_owner_search', 'Search colleagues…'),
                                        empty: t('leads.sidebar.lead_owner_empty', 'No colleagues found.'),
                                        suggestedHeading: t('leads.sidebar.lead_owner_suggested', 'Sales & management'),
                                        peopleHeading: t('leads.sidebar.lead_owner_others', 'Other colleagues'),
                                        unassignedOption: t('leads.sidebar.no_owner', 'No owner assigned'),
                                    }}
                                />
                            </div>
                        </div>

                        <div className="flex items-start gap-2.5">
                            <Mail className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                            <div className="min-w-0">
                                <div className="text-muted-foreground text-xs font-medium">{t('leads.sidebar.email', 'Email')}</div>
                                <div className="truncate text-sm font-medium">
                                    {lead.person?.email ? (
                                        <a href={`mailto:${lead.person.email}`} className="text-primary hover:underline">
                                            {lead.person.email}
                                        </a>
                                    ) : (
                                        <span className="text-muted-foreground">—</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex items-start gap-2.5">
                            <Phone className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                            <div className="min-w-0">
                                <div className="text-muted-foreground text-xs font-medium">{t('leads.sidebar.phone', 'Phone')}</div>
                                <div className="text-sm font-medium">
                                    {contactPhone ? (
                                        <a href={`tel:${contactPhone}`} className="text-primary hover:underline">
                                            {contactPhone}
                                        </a>
                                    ) : (
                                        <span className="text-muted-foreground">—</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle>{t('leads.sidebar.address', 'Address')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {hasAddressOrCoordinates && (
                            <AddressMapThumbnail
                                address={[lead.address_street, lead.address_house_number, lead.address_city].filter(Boolean).join(', ')}
                                lat={lead.address_lat ?? undefined}
                                lng={lead.address_lng ?? undefined}
                            />
                        )}
                        <div className="space-y-4">
                            <DetailField icon={<Globe className="h-4 w-4" />} label={t('leads.sidebar.country', 'Country')}>
                                {lead.country ? getCountryName(lead.country, settings.countries) || lead.country : null}
                            </DetailField>
                            <DetailField icon={<MapPin className="h-4 w-4" />} label={t('leads.sidebar.address_label', 'Address')}>
                                {addressLabel || null}
                            </DetailField>
                            <DetailField icon={<House className="h-4 w-4" />} label={t('leads.sidebar.city', 'City')}>
                                {lead.address_city ? String(lead.address_city) : null}
                            </DetailField>
                            <DetailField icon={<Building2 className="h-4 w-4" />} label={t('leads.sidebar.municipality', 'Municipality')}>
                                {lead.address_municipality ? String(lead.address_municipality) : null}
                            </DetailField>
                            <DetailField icon={<Mailbox className="h-4 w-4" />} label={t('leads.sidebar.postal_code', 'Postal Code')}>
                                {lead.address_postal_code ? String(lead.address_postal_code) : null}
                            </DetailField>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

export default function ShowLead() {
    const { props } = usePage<ShowLeadPageProps & { auth: { user: User } }>();
    // companies and calculationRequestOptions arrive as deferred props, so they are
    // briefly absent on first paint.
    const {
        lead,
        companies = [],
        people,
        users,
        auth,
        latestApprovedOffer,
        linkedCalculationRequests = [],
        calculationRequestOptions = [],
        calculationRequestTableOptions = { statuses: [], phases: [], complexities: [], engineers: [] },
        canViewCalculationFinancials = false,
        canLinkCalculationRequests = false,
        calculationRequestFormOptions = { phases: [], leads: [] },
        contactCandidates = [],
        ownerCandidates = { people: [], suggested_ids: [] },
        projectFormDefaults = null,
        canChangeOwner = false,
    } = props;
    const { t } = useTranslations();
    const isTabletOrMobile = useIsTabletOrMobile();
    const { can } = useCan();
    const { shouldShowForNavKey, isAdminOnlyHiddenNavKey, tabDisplayLabel } = useNavigationVisibilityUi();

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: t('leads.index.breadcrumbs.leads', 'Leads'),
            href: '/leads',
        },
        {
            title: lead.lead_number || lead.id,
            href: '#',
        },
    ];

    const tabs = useMemo(() => {
        type Row = {
            value: string;
            navKey?: NavVisibilityKey;
            label: string;
            count?: number;
            content: ReactNode;
        };

        const rows: Row[] = [
            {
                value: 'details',
                label: t('leads.show.tabs.overview', 'Overview'),
                content: (
                    <LeadDetailsCards
                        lead={lead}
                        latestApprovedOffer={latestApprovedOffer}
                        users={users}
                        currentUser={auth.user}
                        contactCandidates={contactCandidates}
                        ownerCandidates={ownerCandidates}
                        canChangeOwner={canChangeOwner}
                    />
                ),
            },
            {
                value: 'offers',
                label: t('leads.show.tabs.offers', 'Offers'),
                count: lead.offers?.length || 0,
                content: <OffersList lead={lead} offers={lead.offers || []} />,
            },
            {
                value: 'calculations',
                label: t('leads.show.tabs.calculations', 'Calculations'),
                count: linkedCalculationRequests.length,
                content: (
                    <LeadCalculationRequests
                        leadId={lead.id}
                        linked={linkedCalculationRequests}
                        options={calculationRequestOptions}
                        tableOptions={calculationRequestTableOptions}
                        canViewFinancials={canViewCalculationFinancials}
                        canLink={canLinkCalculationRequests}
                        canUpdateStatus={can('calculation-requests.update')}
                        canAssign={can('calculation-requests.assign')}
                    />
                ),
            },
        ];

        return rows
            .filter((row) => shouldShowForNavKey(row.navKey))
            .map(
                (row): LeadTab => ({
                    value: row.value,
                    label: row.label,
                    count: row.count,
                    displayLabel: tabDisplayLabel(row.label, row.navKey, row.count),
                    hiddenFromMainNav: isAdminOnlyHiddenNavKey(row.navKey),
                    content: row.content,
                }),
            );
    }, [
        auth.user,
        can,
        calculationRequestOptions,
        calculationRequestTableOptions,
        canViewCalculationFinancials,
        canLinkCalculationRequests,
        canChangeOwner,
        calculationRequestFormOptions,
        contactCandidates,
        ownerCandidates,
        isAdminOnlyHiddenNavKey,
        latestApprovedOffer,
        lead,
        linkedCalculationRequests,
        shouldShowForNavKey,
        tabDisplayLabel,
        t,
        users,
    ]);

    const defaultTab = useMemo(() => {
        if (tabs.some((tab) => tab.value === 'details')) {
            return 'details';
        }

        return tabs[0]?.value ?? 'details';
    }, [tabs]);

    const [activeTab, setActiveTab] = useState(defaultTab);
    const [mobileActionsDrawerOpen, setMobileActionsDrawerOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    // The action modals used to be mounted three times over — once for the
    // desktop bar, once for the mobile bar, once inside the actions drawer —
    // each with its own live form. They are now mounted once and driven from
    // here, which also lets the actions drawer finish closing before one of
    // them opens; on mobile they are sheets too, and two at once leaves the
    // page with a stuck pointer-events lock.
    const [offerModalOpen, setOfferModalOpen] = useState(false);
    const [editLeadOpen, setEditLeadOpen] = useState(false);
    const [convertProjectOpen, setConvertProjectOpen] = useState(false);
    const [createCalculationOpen, setCreateCalculationOpen] = useState(false);

    /**
     * Closes the actions sheet, then opens the chosen one once it has animated
     * out. The delay is not cosmetic: these are sheets on mobile too, and
     * mounting the second while the first is still leaving stacks two overlays
     * and leaves the body with a stuck pointer-events lock.
     *
     * The wait cannot hang off the drawer's onOpenChange — vaul only fires that
     * for closes it initiates itself, not for a controlled prop change.
     */
    const requestActionFromDrawer = (action: 'offer' | 'edit' | 'project' | 'calculation' | 'delete') => {
        setMobileActionsDrawerOpen(false);

        window.setTimeout(() => {
            if (action === 'offer') setOfferModalOpen(true);
            if (action === 'edit') setEditLeadOpen(true);
            if (action === 'project') setConvertProjectOpen(true);
            if (action === 'calculation') setCreateCalculationOpen(true);
            if (action === 'delete') setDeleteConfirmOpen(true);
        }, 350);
    };

    const desktopMaxVisibleTabs = 4;
    const mainNavTabs = tabs.filter((tab) => !tab.hiddenFromMainNav);
    const hiddenNavTabs = tabs.filter((tab) => tab.hiddenFromMainNav);
    const visibleTabs = isTabletOrMobile ? mainNavTabs : mainNavTabs.slice(0, desktopMaxVisibleTabs);
    const overflowTabs = isTabletOrMobile ? hiddenNavTabs : [...mainNavTabs.slice(desktopMaxVisibleTabs), ...hiddenNavTabs];
    const hasOverflow = overflowTabs.length > 0;
    const tabIcons: Record<string, ReactNode> = {
        details: <Info className="h-4 w-4" />,
        offers: <FileText className="h-4 w-4" />,
        calculations: <Calculator className="h-4 w-4" />,
    };

    const sellingClosed = isSellingClosed(lead);

    // An approved offer is what fixes the price the project is created at, so it
    // gates the conversion here as well as server-side. Deliberately unaffected by
    // the win: converting is what a won lead is still for.
    const canConvertToProject = Boolean(latestApprovedOffer && latestApprovedOffer.status === 'approved' && can('projects.create') && !lead.project);
    const canUpdateLead = can('leads.update') && !lead.project;
    const canDeleteLead = can('leads.delete');
    const canViewCosts = can('costs.leads.view');
    // Both stop at the win rather than at the conversion: the agreed price is the
    // one on file, so there is nothing left to quote or to price. Editing the lead
    // itself is a separate matter and stays open until it becomes a project.
    const canCreateOffer = can('leads.update') && !sellingClosed;
    // Raising a calculation is a lead-level action, so it sits on the page's
    // action row rather than inside the calculations tab.
    const canCreateCalculation = can('calculation-requests.create') && !sellingClosed;

    // The lead's commercial figures in full — this strip is the only place they
    // are shown, so nothing here is a duplicate of a card further down.
    const headlineTotals = useMemo(() => {
        const budget = Number(lead.estimated_value || 0);
        const totalCosts =
            latestApprovedOffer && latestApprovedOffer.status === 'approved'
                ? latestApprovedOffer.total_costs !== undefined
                    ? Number(latestApprovedOffer.total_costs) || 0
                    : (latestApprovedOffer.costLines?.reduce((sum, cost) => sum + cost.amount, 0) ?? 0)
                : Number(lead.total_costs || 0);

        const profit = budget - totalCosts;

        return { budget, totalCosts, profit, profitPercent: budget > 0 ? (profit / budget) * 100 : 0 };
    }, [lead.estimated_value, lead.total_costs, latestApprovedOffer]);

    const metricTiles = [
        <MetricTile
            key="estimated_value"
            icon={<Wallet className="h-5 w-5" />}
            tone="bg-primary/12 text-primary"
            label={t('leads.sidebar.estimated_value', 'Estimated Value')}
        >
            {formatCurrency(headlineTotals.budget)}
        </MetricTile>,
        ...(canViewCosts
            ? [
                  <MetricTile
                      key="total_costs"
                      icon={<Receipt className="h-5 w-5" />}
                      tone="bg-cat-purple text-cat-purple-foreground"
                      label={t('leads.sidebar.total_costs', 'Total Costs')}
                  >
                      {formatCurrency(headlineTotals.totalCosts)}
                  </MetricTile>,
                  <MetricTile
                      key="profit"
                      icon={<TrendingUp className="h-5 w-5" />}
                      tone="bg-success-subtle text-success-subtle-foreground"
                      label={t('leads.sidebar.profit', 'Profit')}
                  >
                      <span className={headlineTotals.profit >= 0 ? 'text-success-subtle-foreground' : 'text-destructive'}>
                          {formatCurrency(headlineTotals.profit)}{' '}
                          <span className="text-sm font-normal">
                              ({headlineTotals.profitPercent >= 0 ? '+' : ''}
                              {headlineTotals.profitPercent.toFixed(1)}%)
                          </span>
                      </span>
                  </MetricTile>,
              ]
            : []),
        <MetricTile
            key="offer_due_date"
            icon={<CalendarClock className="h-5 w-5" />}
            tone="bg-info-subtle text-info-subtle-foreground"
            label={t('leads.sidebar.offer_due_date', 'Offer deadline')}
        >
            {lead.offer_due_date ? formatDateForDisplay(String(lead.offer_due_date).slice(0, 10)) : <span className="text-muted-foreground">—</span>}
        </MetricTile>,
        // Priority and status stay inline-editable, as they were before the redesign.
        <MetricTile
            key="priority"
            icon={<Flag className="h-5 w-5" />}
            tone="bg-warning-subtle text-warning-subtle-foreground"
            label={t('leads.sidebar.priority', 'Priority')}
        >
            <UnifiedPrioritySelector
                value={lead.priority}
                priorityType="lead"
                mode="editable"
                updateRoute="leads.update-priority"
                resourceId={lead.id}
            />
        </MetricTile>,
        <MetricTile key="status" icon={<Info className="h-5 w-5" />} tone="bg-primary/12 text-primary" label={t('leads.sidebar.status', 'Status')}>
            <UnifiedStatusSelector value={lead.status} statusType="lead" mode="editable" updateRoute="leads.update-status" resourceId={lead.id} />
        </MetricTile>,
    ];

    /**
     * Rendered in full on the tabs row — every action stays visible rather than
     * collapsing into an overflow menu.
     */
    const desktopActions = (
        <div className="flex flex-wrap items-center justify-end gap-2">
            {canCreateCalculation && (
                <Button type="button" variant="outline" size="sm" className="gap-2 whitespace-nowrap" onClick={() => setCreateCalculationOpen(true)}>
                    <Calculator className="h-4 w-4" />
                    {t('leads.calculations.create', 'New calculation')}
                </Button>
            )}
            {canConvertToProject && (
                <Button type="button" variant="outline" size="sm" className="gap-2 whitespace-nowrap" onClick={() => setConvertProjectOpen(true)}>
                    <TrendingUp className="h-4 w-4" />
                    {t('leads.sidebar.convert_to_project', 'Convert to Project')}
                </Button>
            )}
            {canCreateOffer && (
                <Button type="button" variant="outline" size="sm" className="gap-2 whitespace-nowrap" onClick={() => setOfferModalOpen(true)}>
                    <FileText className="h-4 w-4" />
                    {t('leads.sidebar.add_offer', 'Add Offer')}
                </Button>
            )}
            {!canConvertToProject && !canUpdateLead && (
                <Button type="button" variant="outline" size="sm" className="whitespace-nowrap" disabled>
                    {lead.project
                        ? t('leads.sidebar.converted_to_project', 'Lead has been converted to a project, no actions available')
                        : t('common.no_actions_available', 'No actions available')}
                </Button>
            )}
            {/* Edit → Delete close the row, in that order, on every detail page. */}
            {canUpdateLead && (
                <Button type="button" variant="outline" size="sm" className={DETAIL_ACTION_BUTTON_CLASS} onClick={() => setEditLeadOpen(true)}>
                    <Edit className="h-4 w-4" />
                    {t('common.edit', 'Edit')}
                </Button>
            )}
            {canDeleteLead && <DeleteActionButton label={t('leads.show.actions.delete', 'Delete Lead')} onClick={() => setDeleteConfirmOpen(true)} />}
        </div>
    );

    return (
        <>
            <Head title={t('leads.show.title', 'Lead')} />

            <ShowLayout breadcrumbs={breadcrumbs}>
                {/* Title row: identity and status. */}
                <div className="mb-4 min-w-0">
                    <div className="flex min-w-0 items-center gap-3">
                        <h1 className="truncate text-3xl font-bold tracking-tight">{lead.lead_number || lead.id}</h1>
                        <UnifiedStatusSelector value={lead.status} statusType="lead" mode="readonly" />
                    </div>
                    {/* The number identifies the lead; the object name says what it is. */}
                    {lead.name ? <p className="text-muted-foreground mt-1 truncate text-sm">{lead.name}</p> : null}
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 w-full flex-1 flex-col">
                    {/* Tabs left, actions right, on one line. */}
                    <div className="mb-4 flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
                        <div className="min-w-0">
                            {isTabletOrMobile ? (
                                <div className="-mx-1 overflow-x-auto pb-1">
                                    <div className="flex w-max min-w-full items-stretch gap-2 px-1">
                                        {[...visibleTabs, ...overflowTabs].map((tab) => {
                                            const isActive = activeTab === tab.value;

                                            return (
                                                <button
                                                    key={tab.value}
                                                    type="button"
                                                    onClick={() => setActiveTab(tab.value)}
                                                    className={cn(
                                                        'h-auto min-w-[120px] rounded-xl border px-4 py-3 text-left transition-colors',
                                                        isActive
                                                            ? 'border-primary/40 bg-primary/10 text-primary'
                                                            : 'border-border bg-background text-foreground',
                                                    )}
                                                >
                                                    <span className="flex flex-col items-start gap-1.5">
                                                        <span className="text-muted-foreground">{tabIcons[tab.value]}</span>
                                                        <span className="text-sm leading-tight font-medium">{tab.displayLabel}</span>
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <TabsList
                                    className={cn(
                                        'bg-muted flex h-auto min-w-0 flex-1 flex-nowrap items-center gap-1 rounded-xl px-1 py-1 sm:w-fit sm:flex-initial',
                                    )}
                                >
                                    {visibleTabs.map((tab) => (
                                        <TabsTrigger key={tab.value} value={tab.value} className={cn('rounded-md text-sm')}>
                                            <span className="inline-flex items-center gap-2">
                                                {tabIcons[tab.value]}
                                                {tab.displayLabel}
                                            </span>
                                        </TabsTrigger>
                                    ))}
                                    {hasOverflow && (
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <button
                                                    type="button"
                                                    className={cn(
                                                        'focus-visible:ring-ring inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
                                                        overflowTabs.find((x) => x.value === activeTab)
                                                            ? 'bg-secondary text-secondary-foreground'
                                                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                                                    )}
                                                >
                                                    {overflowTabs.find((x) => x.value === activeTab)
                                                        ? overflowTabs.find((x) => x.value === activeTab)?.displayLabel
                                                        : t('work_orders.show.tabs.more', '+{count} more').replace(
                                                              '{count}',
                                                              String(overflowTabs.length),
                                                          )}
                                                </button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="min-w-[150px]">
                                                {overflowTabs.map((tab) => (
                                                    <DropdownMenuItem
                                                        key={tab.value}
                                                        onClick={() => setActiveTab(tab.value)}
                                                        className={cn(activeTab === tab.value && 'bg-accent font-medium')}
                                                    >
                                                        <span className="inline-flex items-center gap-2">
                                                            {tabIcons[tab.value]}
                                                            {tab.displayLabel}
                                                        </span>
                                                    </DropdownMenuItem>
                                                ))}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    )}
                                </TabsList>
                            )}
                        </div>

                        <div className="hidden lg:block">{desktopActions}</div>
                    </div>

                    {/* Headline metrics stay above the fold on every tab. */}
                    <div
                        className={cn(
                            'bg-card mb-4 grid grid-cols-1 divide-y overflow-hidden rounded-xl border sm:grid-cols-2 sm:divide-x xl:divide-y-0',
                            canViewCosts ? 'xl:grid-cols-6' : 'xl:grid-cols-4',
                        )}
                    >
                        {metricTiles}
                    </div>

                    {tabs.map((tab) => (
                        <TabsContent key={tab.value} value={tab.value} className="mt-0 outline-none data-[state=inactive]:hidden">
                            {tab.content}
                        </TabsContent>
                    ))}
                </Tabs>
            </ShowLayout>
            {isTabletOrMobile && (
                <div className="bg-background/95 supports-[backdrop-filter]:bg-background/60 safe-area-inset-bottom fixed right-0 bottom-0 left-0 z-50 border-t p-4 shadow-lg backdrop-blur lg:hidden">
                    <div className="grid grid-cols-5 gap-2">
                        {canConvertToProject ? (
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-11 w-full rounded-xl"
                                onClick={() => setConvertProjectOpen(true)}
                                aria-label={t('leads.sidebar.convert_to_project', 'Convert to Project')}
                            >
                                <TrendingUp className="h-4 w-4" />
                            </Button>
                        ) : (
                            <div />
                        )}
                        {canCreateOffer ? (
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-11 w-full rounded-xl"
                                onClick={() => setOfferModalOpen(true)}
                                aria-label={t('leads.sidebar.add_offer', 'Add Offer')}
                            >
                                <FileText className="h-4 w-4" />
                            </Button>
                        ) : (
                            <div />
                        )}
                        {canUpdateLead ? (
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-11 w-full rounded-xl"
                                onClick={() => setEditLeadOpen(true)}
                                aria-label={t('leads.sidebar.edit_lead', 'Edit Lead')}
                            >
                                <Edit className="h-4 w-4" />
                            </Button>
                        ) : (
                            <div />
                        )}
                        <div />
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-11 w-full rounded-xl"
                            onClick={() => setMobileActionsDrawerOpen(true)}
                            aria-label={t('common.more_actions', 'More actions')}
                        >
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}
            <Drawer open={mobileActionsDrawerOpen} onOpenChange={setMobileActionsDrawerOpen} direction="bottom">
                <DrawerContent className="lg:hidden">
                    <DrawerHeader>
                        <DrawerTitle>{t('leads.sidebar.actions', 'Actions')}</DrawerTitle>
                        <DrawerDescription>{t('common.select_action', 'Select an action')}</DrawerDescription>
                    </DrawerHeader>
                    <div className="space-y-1 p-4 pt-0">
                        {canCreateCalculation && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="h-11 w-full justify-start gap-2"
                                onClick={() => requestActionFromDrawer('calculation')}
                            >
                                <Calculator className="h-4 w-4" />
                                {t('leads.calculations.create', 'New calculation')}
                            </Button>
                        )}
                        {canConvertToProject && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="h-11 w-full justify-start gap-2"
                                onClick={() => requestActionFromDrawer('project')}
                            >
                                <TrendingUp className="h-4 w-4" />
                                {t('leads.sidebar.convert_to_project', 'Convert to Project')}
                            </Button>
                        )}
                        {canCreateOffer && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="h-11 w-full justify-start gap-2"
                                onClick={() => requestActionFromDrawer('offer')}
                            >
                                <FileText className="h-4 w-4" />
                                {t('leads.sidebar.add_offer', 'Add Offer')}
                            </Button>
                        )}
                        {canUpdateLead && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="h-11 w-full justify-start gap-2"
                                onClick={() => requestActionFromDrawer('edit')}
                            >
                                <Edit className="h-4 w-4" />
                                {t('common.edit', 'Edit')}
                            </Button>
                        )}
                        {!canConvertToProject && !canUpdateLead && (
                            <Button type="button" variant="ghost" className="h-11 w-full justify-start" disabled>
                                {lead.project
                                    ? t('leads.sidebar.converted_to_project', 'Lead has been converted to a project, no actions available')
                                    : t('common.no_actions_available', 'No actions available')}
                            </Button>
                        )}
                        {canDeleteLead && (
                            <Button
                                type="button"
                                variant="ghost"
                                className="text-destructive hover:text-destructive h-11 w-full justify-start gap-2"
                                onClick={() => requestActionFromDrawer('delete')}
                            >
                                <Trash2 className="h-4 w-4" />
                                {t('leads.show.actions.delete', 'Delete Lead')}
                            </Button>
                        )}
                    </div>
                </DrawerContent>
            </Drawer>

            {/* Mounted once, driven by the buttons in the desktop bar, the mobile
                bar and the actions sheet alike. */}
            {canConvertToProject && projectFormDefaults && (
                <CreateProjectFromLeadSheet
                    defaults={projectFormDefaults}
                    ownerCandidates={ownerCandidates}
                    open={convertProjectOpen}
                    onOpenChange={setConvertProjectOpen}
                />
            )}
            {canCreateOffer && <CreateOfferModal lead={lead} open={offerModalOpen} onOpenChange={setOfferModalOpen} />}
            {canUpdateLead && (
                <EditLeadModal lead={lead} companies={companies} people={people} users={users} open={editLeadOpen} onOpenChange={setEditLeadOpen} />
            )}
            {canCreateCalculation && (
                <CreateCalculationRequestSheet
                    open={createCalculationOpen}
                    onOpenChange={setCreateCalculationOpen}
                    leadId={lead.id}
                    options={calculationRequestFormOptions}
                />
            )}
            {isTabletOrMobile && <div className="h-24 lg:h-0" />}

            <DeleteConfirmAlert
                open={deleteConfirmOpen}
                onOpenChange={setDeleteConfirmOpen}
                title={t('leads.show.delete.confirm_title', 'Delete Lead')}
                description={t(
                    'leads.show.delete.confirm_description',
                    'Are you sure you want to delete ":name"? This action cannot be undone.',
                ).replace(':name', lead.lead_number || lead.id)}
                cancelLabel={t('leads.show.delete.cancel', 'Cancel')}
                confirmLabel={t('leads.show.delete.confirm_button', 'Delete')}
                onConfirm={() => {
                    setDeleteConfirmOpen(false);
                    router.delete(route('leads.destroy', lead.id), {
                        onSuccess: () => toast.success(t('leads.show.delete.success', 'Lead deleted.')),
                        onError: () => toast.error(t('leads.show.delete.failed', 'Failed to delete lead.')),
                    });
                }}
            />
        </>
    );
}
